import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import { MoreHorizontal, Ban, CheckCircle2, Eye, RotateCcw, XCircle } from 'lucide-react'

import type { ApplicationResponseDTO } from '@/utils/api/membership-service/models'
import { RenewalStatusBadge } from './RenewalStatusBadge'
import { formatRenewalDate } from '../utils'
import { isValid, parseISO } from 'date-fns'

export interface RenewalColumnOptions {
  onApprove: (row: ApplicationResponseDTO) => void
  onReject: (row: ApplicationResponseDTO) => void
  onActivate: (row: ApplicationResponseDTO) => void
  onDeactivate: (row: ApplicationResponseDTO) => void
  onViewProof: (row: ApplicationResponseDTO) => void
  disableActions?: boolean
}

function getNormalizedStatus(status: string | undefined): string {
  return status?.toUpperCase?.() ?? ''
}

function formatFullName(
  firstName?: string,
  middleName?: string | null,
  lastName?: string,
): string {
  const parts = [firstName, middleName, lastName].filter((part) => part && part.trim().length > 0)
  return parts.join(' ') || 'Unnamed applicant'
}

function computeDurationLabel(startDate?: string | null, endDate?: string | null): string {
  if (!startDate || !endDate) return '—'
  
  const start = parseISO(startDate)
  const end = parseISO(endDate)
  if (!isValid(start) || !isValid(end)) return '—'

  const diffMs = end.getTime() - start.getTime()
  if (diffMs <= 0) return '—'

  const totalDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1
  const months = Math.floor(totalDays / 30)
  const years = Math.floor(months / 12)
  if (years >= 1) {
    return `${years} year${years === 1 ? '' : 's'}`
  }
  if (months >= 1) {
    return `${months} month${months === 1 ? '' : 's'}`
  }

  return `${totalDays} day${totalDays === 1 ? '' : 's'}`
}

function computeRemainingDurationLabel(endDate?: string | null): string {
  if (!endDate) return '—'
  
  const end = parseISO(endDate)
  if (!isValid(end)) return '—'

  const now = new Date()
  const diffMs = end.getTime() - now.getTime()
  if (diffMs <= 0) return '0 days'

  const totalDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  const months = Math.floor(totalDays / 30)
  const years = Math.floor(months / 12)

  if (years >= 1) {
    return `${years} year${years === 1 ? '' : 's'}`
  }
  if (months >= 1) {
    return `${months} month${months === 1 ? '' : 's'}`
  }

  return `${totalDays} day${totalDays === 1 ? '' : 's'}`
}

export function createRenewalColumns(
  options: RenewalColumnOptions,
): ColumnConfig<ApplicationResponseDTO>[] {
  const { disableActions = false, ...handlers } = options

  return [
    {
      key: 'name',
      header: 'Applicant',
      accessor: (row) => formatFullName(row.firstName, row.middleName, row.lastName),
      sortable: true,
      filterable: true,
    },
    {
      key: 'phone',
      header: 'Phone',
      accessor: (row) => row.phoneNumbers || '—',
      filterable: true,
    },
    {
      key: 'category',
      header: 'Category',
      accessor: (row) => row.categoryName || 'Uncategorised',
      sortable: true,
      filterable: true,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.status || '',
      sortable: true,
      filterable: true,
      cell: (_value, row) => <RenewalStatusBadge status={row.status || ''} />,
    },
    {
      key: 'startDateDisplay',
      header: 'Start date',
      accessor: (row) => formatRenewalDate(row.membershipStartAt ?? null),
      sortable: true,
    },
    {
      key: 'endDateDisplay',
      header: 'End date',
      accessor: (row) => formatRenewalDate(row.membershipExpiryAt ?? null),
      sortable: true,
    },
    {
      key: 'amount',
      header: 'Amount Paid',
      accessor: (row) => row.amount ?? 0,
      sortable: true,
      filterable: true,
      cell: (value) => {
        const amount = value as number
        return amount > 0 ? `UGX ${amount.toLocaleString()}` : '—'
      },
    },
    {
      key: 'paymentStatus',
      header: 'Payment Status',
      accessor: () => 'Full',
      sortable: true,
      filterable: true,
      cell: (_value, row) => {
        const amount = row.amount ?? 0
        const hasProof = row.documents?.some(
          (doc) => doc.documentType?.toUpperCase() === 'PROOF OF PAYMENT'
        )
        
        let status = 'Outstanding'
        let variant: 'default' | 'secondary' | 'destructive' | 'outline' = 'outline'
        
        if (amount > 0 && hasProof) {
          status = 'Full'
          variant = 'default'
        } else if (amount > 0) {
          status = 'Partial'
          variant = 'secondary'
        }
        
        return <Badge variant={variant}>{status}</Badge>
      },
    },
    {
      key: 'duration',
      header: 'Duration',
      accessor: (row) => computeDurationLabel(row.membershipStartAt, row.membershipExpiryAt),
      sortable: true,
      cell: (_value, row) => {
        const duration = computeDurationLabel(row.membershipStartAt, row.membershipExpiryAt)
        return duration !== '—' ? (
          <Badge variant="outline" className="bg-primary/5 text-primary">
            {duration}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      key: 'remainingDuration',
      header: 'Remaining Duration',
      accessor: (row) => computeRemainingDurationLabel(row.membershipExpiryAt),
      sortable: true,
      cell: (_value, row) => {
        const remainingDuration = computeRemainingDurationLabel(row.membershipExpiryAt)
        return remainingDuration !== '—' ? (
          <Badge variant="outline" className="bg-primary/5 text-primary">
            {remainingDuration}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      key: 'actions',
      header: '',
      accessor: () => null,
      sortable: false,
      filterable: false,
      cell: (_value, row) => {
        const status = getNormalizedStatus(row.status)
        const canApprove = status === 'PENDING' || status === 'REJECTED'
        const canReject = status === 'PENDING' || status === 'APPROVED' || status === 'RENEWED'
        const canActivate = status === 'APPROVED' || status === 'PENDING'
        const canDeactivate = status === 'RENEWED'
        const proofOfPaymentDoc = row.documents?.find(
          (doc) => doc.documentType?.toLocaleUpperCase?.() === 'PROOF OF PAYMENT'
        )
        const hasProof = Boolean(proofOfPaymentDoc?.fileUrl)

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={disableActions}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Open actions menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  handlers.onApprove(row)
                }}
                disabled={!canApprove}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve renewal
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  handlers.onReject(row)
                }}
                disabled={!canReject}
              >
                <XCircle className="mr-2 h-4 w-4 text-destructive" />
                Reject renewal
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  handlers.onActivate(row)
                }}
                disabled={!canActivate}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Activate membership
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  handlers.onDeactivate(row)
                }}
                disabled={!canDeactivate}
              >
                <Ban className="mr-2 h-4 w-4 text-destructive" />
                Deactivate membership
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  handlers.onViewProof(row)
                }}
                disabled={!hasProof}
              >
                <Eye className="mr-2 h-4 w-4" />
                View proof of payment
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]
}
